import { useRouter } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui/button";
import { Copy as CopyIcon, Link2 } from "lucide-react";
import { useState, type RefObject } from "react";
import type { ElementType } from "react";
import QRCodeImport from "react-qr-code";
import { toast } from "@patient-watch/ui/sonner";
import { getToastCopy, useToastCopy } from "@/features/i18n/toastCopy";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { cn } from "@/lib/utils";
import type { ToT } from "../../../route-types";

/** CJS package: default import is sometimes `{ default, QRCode }`, not the component (invalid element type). */
const QRCode = (
    typeof QRCodeImport === "function" ||
    (typeof QRCodeImport === "object" && QRCodeImport !== null && "$$typeof" in QRCodeImport)
        ? QRCodeImport
        : (QRCodeImport as unknown as { default: ElementType }).default
) as typeof QRCodeImport;

async function svgStringToPngBlob(svgString: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        img.onload = () => {
            const w = img.naturalWidth || 256;
            const h = img.naturalHeight || 256;
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                URL.revokeObjectURL(url);
                reject(new Error("Could not draw QR code"));
                return;
            }
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not create image"))), "image/png");
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Could not load QR image"));
        };
        img.src = url;
    });
}

export async function copyQrSvgToClipboard(svgEl: SVGSVGElement | null) {
    const toastCopy = getToastCopy();
    if (!svgEl) {
        toast.error(toastCopy.qrCodeNotReady);
        return;
    }
    const svgString = new XMLSerializer().serializeToString(svgEl);
    const pngBlob = await svgStringToPngBlob(svgString);
    try {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
        toast.info(toastCopy.qrCodeCopiedToClipboard);
    } catch {
        toast.error(toastCopy.couldNotCopyQrImage);
    }
}

export function InviteShareButtons({
    destinationUrl,
    qrContainerRef,
    className,
    showCopyQr = true,
}: {
    destinationUrl: string;
    qrContainerRef: RefObject<HTMLDivElement | null>;
    className?: string;
    /** When false, only show Copy link as the primary action (study invites). */
    showCopyQr?: boolean;
}) {
    const toastCopy = useToastCopy();
    const inviteCopy = useInviteCopy();
    const copyLink = (e: React.MouseEvent<HTMLButtonElement>) => {
        void navigator.clipboard.writeText(destinationUrl);
        toast.info(toastCopy.linkCopiedToClipboard);
        e.currentTarget.classList.add("animate-wiggle");
        const btn = e.currentTarget;
        setTimeout(() => btn.classList.remove("animate-wiggle"), 600);
    };

    const copyQr = (e: React.MouseEvent<HTMLButtonElement>) => {
        const svg = qrContainerRef.current?.querySelector("svg");
        void copyQrSvgToClipboard(svg ?? null);
        e.currentTarget.classList.add("animate-wiggle");
        const btn = e.currentTarget;
        setTimeout(() => btn.classList.remove("animate-wiggle"), 600);
    };

    return (
        <div className={cn(showCopyQr ? "flex sm:flex-row flex-col gap-2 sm:gap-3" : "w-full", className)}>
            <Button
                type="button"
                variant={showCopyQr ? "outline" : "default"}
                size="default"
                className={cn("gap-2", showCopyQr ? "flex-1" : "w-full")}
                onClick={copyLink}
                data-testid="study-invite-copy-link"
            >
                <Link2 className="w-4 h-4" aria-hidden />
                {inviteCopy.copyLink}
            </Button>
            {showCopyQr ? (
                <Button type="button" variant="outline" size="default" className="flex-1 gap-2" onClick={copyQr}>
                    <CopyIcon className="w-4 h-4" aria-hidden />
                    {inviteCopy.copyQrCode}
                </Button>
            ) : null}
        </div>
    );
}

export function InviteQrMark({
    destinationUrl,
    title,
    variant = "large",
    className,
    containerRef,
}: {
    destinationUrl: string;
    title: string;
    variant?: "large" | "compact";
    className?: string;
    containerRef?: RefObject<HTMLDivElement | null>;
}) {
    const isCompact = variant === "compact";
    return (
        <div
            ref={containerRef}
            className={cn(
                "flex justify-center items-center bg-white dark:bg-slate-950",
                isCompact
                    ? "shadow-sm p-2 border border-slate-200/80 dark:border-slate-700 rounded-xl h-[min(72vw,260px)] w-[min(72vw,260px)] sm:h-[240px] sm:w-[240px] md:h-[250px] md:w-[250px] lg:h-[300px] lg:w-[300px] shrink-0"
                    : "shadow-lg p-4 border border-slate-200/80 dark:border-slate-700 rounded-xl w-[70vw] md:w-[300px] lg:w-[400px] h-[70vw] md:h-[300px] lg:h-[400px]",
                className
            )}
        >
            <QRCode
                value={destinationUrl}
                size={isCompact ? 300 : 220}
                className=""
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                viewBox="0 0 256 256"
                title={title}
            />
        </div>
    );
}

export function InviteLinkCopy({ destinationUrl, className }: { destinationUrl: string; className?: string }) {
    const toastCopy = useToastCopy();
    const inviteCopy = useInviteCopy();
    const [copied, setCopied] = useState(false);

    function handleCopy() {
        void navigator.clipboard.writeText(destinationUrl);
        setCopied(true);
        toast.success(toastCopy.copiedToClipboard);
        setTimeout(() => setCopied(false), 1000);
    }

    return (
        <button
            type="button"
            className={cn(
                "flex justify-between items-center gap-1.5 px-2 py-1.5 border rounded-md w-full min-w-0 font-mono text-xs text-left transition-colors cursor-pointer select-all",
                "border-slate-200/80 bg-slate-50/50 text-slate-600 dark:border-slate-700 dark:bg-slate-900/35 dark:text-slate-400",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400/70 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-slate-950",
                copied
                    ? "border-indigo-300/80 bg-indigo-50/45 animate-wiggle dark:border-indigo-500/45 dark:bg-indigo-950/25"
                    : "hover:border-slate-300/90 hover:bg-slate-100/60 dark:hover:border-slate-600 dark:hover:bg-slate-800/45",
                className
            )}
            onClick={handleCopy}
            aria-label={inviteCopy.copyInviteLink}
        >
            <span className="flex-1 min-w-0 truncate select-all">{destinationUrl}</span>
            <CopyIcon
                size={14}
                className={
                    copied
                        ? "shrink-0 text-indigo-600 dark:text-indigo-400"
                        : "shrink-0 text-slate-400 dark:text-slate-500"
                }
            />
        </button>
    );
}

function QRCodeRoute({ route, title }: { route: ToT; title: string }) {
    const router = useRouter();
    const destinationUrl = window.location.origin + router.buildLocation(route as never).href;

    return (
        <div className="flex flex-col items-center gap-4 mx-auto w-full max-w-md">
            <InviteQrMark destinationUrl={destinationUrl} title={title} variant="large" />
            <InviteLinkCopy
                destinationUrl={destinationUrl}
                className="max-w-[70vw] md:max-w-[300px] lg:max-w-[400px]"
            />
        </div>
    );
}

export default QRCodeRoute;
